/**
 * NavOverflowMenu — automatic "⋯ More" overflow popover for the navbar.
 *
 * Behavior:
 *  - Pills are described as data (id + label + icon + route + priority).
 *  - On every container resize (ResizeObserver), we measure how many pills
 *    fit, hide the overflowed ones, and surface them inside a "⋯ More ▾"
 *    popover that mirrors their look in a vertical list.
 *  - Pill widths are MEASURED ONCE on first paint and cached, so when the
 *    screen widens again the previously-hidden pills (which have display:none
 *    and thus no measurable width) still know their natural size and can
 *    re-appear.
 *  - Lower `priority` = hides FIRST when space runs out. Anchor pills
 *    (Dashboard, Credits, Bell, Logout) stay outside this system and always
 *    stay visible.
 *  - Click outside or Escape key closes the popover.
 *
 * Usage:
 *   const items = [{ id, to, label, icon, title, priority, isActive }, ...];
 *   <NavOverflowRail items={items} testid="nav-overflow-rail" />
 *
 * The component renders the moveable pills inline AND the More menu after
 * them, so it slots cleanly into the existing flex row in Navbar.jsx.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MoreHorizontal, ChevronDown } from "lucide-react";

/** Reserved width (px) for the "⋯ More ▾" button + its surrounding gap.
 *  Subtracted from the container width when deciding overflow. */
const MORE_BUTTON_RESERVE = 88;

export default function NavOverflowRail({ items, testid = "nav-overflow-rail" }) {
  const containerRef = useRef(null);
  const railRef = useRef(null);
  const measuredWidths = useRef({}); // { id -> px }
  const pillRefs = useRef({});       // { id -> HTMLAnchorElement }
  const [hiddenIds, setHiddenIds] = useState(new Set());
  const [open, setOpen] = useState(false);
  const popoverRef = useRef(null);

  // Capture each pill's intrinsic width on first paint. After this, pills can
  // be display:none (zero offsetWidth) without losing their cached width.
  useLayoutEffect(() => {
    items.forEach((it) => {
      const el = pillRefs.current[it.id];
      if (el && !measuredWidths.current[it.id]) {
        // Include the right-margin gap so the running total matches the actual
        // rendered row.
        const style = window.getComputedStyle(el);
        const mr = parseFloat(style.marginRight || "0");
        measuredWidths.current[it.id] = el.offsetWidth + mr + 6;
      }
    });
  }, [items]);

  // ResizeObserver — recompute which pills overflow whenever the container
  // resizes. Pills are sorted by priority DESCENDING (highest priority first
  // fills the space; lowest priority is first to overflow).
  useEffect(() => {
    if (!containerRef.current) return undefined;
    const recompute = () => {
      const container = containerRef.current;
      if (!container) return;
      const available = container.clientWidth - MORE_BUTTON_RESERVE;
      if (available <= 0) {
        setHiddenIds(new Set(items.map((i) => i.id)));
        return;
      }
      const sorted = [...items].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
      let used = 0;
      const visible = new Set();
      for (const it of sorted) {
        const w = measuredWidths.current[it.id] || 100; // safe default
        if (used + w <= available) {
          visible.add(it.id);
          used += w;
        }
      }
      const hidden = new Set(items.filter((i) => !visible.has(i.id)).map((i) => i.id));
      setHiddenIds(hidden);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [items]);

  // Close on outside-click + Escape — same a11y pattern used elsewhere.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const overflowedItems = useMemo(
    () => items.filter((i) => hiddenIds.has(i.id)),
    [items, hiddenIds],
  );

  return (
    <div
      ref={containerRef}
      className="flex-1 flex items-center gap-2 min-w-0"
      data-testid={testid}
    >
      <div ref={railRef} className="flex items-center gap-2 flex-wrap-none overflow-hidden">
        {items.map((it) => {
          const Ico = it.icon;
          const isHidden = hiddenIds.has(it.id);
          return (
            <Link
              key={it.id}
              ref={(el) => { pillRefs.current[it.id] = el; }}
              to={it.to}
              data-testid={it.testid || `nav-${it.id}`}
              className={`inline-flex rm-nav-pill ${it.isActive ? "is-active" : ""}`}
              title={it.title}
              style={{
                ...(it.style || {}),
                display: isHidden ? "none" : undefined,
              }}
            >
              {Ico ? <Ico size={14} className={it.iconClassName} /> : null}
              <span>{it.label}</span>
            </Link>
          );
        })}
      </div>

      {overflowedItems.length > 0 && (
        <div ref={popoverRef} className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex rm-nav-pill"
            data-testid="nav-more-toggle"
            title={`${overflowedItems.length} more`}
            aria-expanded={open}
            aria-haspopup="menu"
          >
            <MoreHorizontal size={14} />
            <span className="hidden sm:inline">More</span>
            <ChevronDown
              size={12}
              style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
              className="transition-transform"
            />
          </button>
          {open && (
            <div
              role="menu"
              data-testid="nav-more-menu"
              className="absolute right-0 mt-2 rm-card-static p-1.5 z-50"
              style={{
                background: "white",
                borderColor: "#0F1B2D",
                boxShadow: "5px 5px 0 0 #0F1B2D",
                minWidth: 220,
              }}
            >
              {overflowedItems.map((it) => {
                const Ico = it.icon;
                return (
                  <Link
                    key={it.id}
                    to={it.to}
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    data-testid={`nav-more-${it.id}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm hover:bg-[#FFF7ED] focus:outline-none focus:bg-[#FFF7ED] ${
                      it.isActive ? "font-bold" : ""
                    }`}
                    style={{ color: "#0F1B2D" }}
                  >
                    {Ico ? <Ico size={14} className={it.iconClassName} /> : null}
                    <span>{it.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
